import {
    Box,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectProps,
    ListSubheader,
    SxProps
} from '@mui/material';
import { useId } from 'react';
import { Info } from '../info';

export type Option = {
    label: string;
    value: string | number;
    sx?: SxProps;
    icon?: React.ReactNode;
};

export type CategorizedOption = {
    category: string;
    options: Option[];
};

type SelectFieldProps = Omit<SelectProps, 'margin'> & {
    options?: Option[];
    categorizedOptions?: CategorizedOption[];
    helperText?: string | null;
    margin?: 'none' | 'dense' | 'normal';
    emptyOption?: Option;
    renderCustomContent?: React.ReactNode;
    renderCustomTopContent?: React.ReactNode;
    hiddenOptions?: React.ReactNode;
    noMargin?: boolean;
    renderOption?: (option: Option) => React.ReactNode;
    labelSx?: SxProps;
    /** Help-cms slug; renders the `?` help icon beside the label (see `.cursor/rules/info-help-topics.mdc`). */
    helpTopicPath?: string;
    /** Optional tooltip on the help icon. */
    infoText?: React.ReactNode;
};

export function SelectField({
    options = [],
    categorizedOptions = [],
    emptyOption,
    size = 'small',
    margin = 'normal',
    label,
    name,
    helperText,
    readOnly = false,
    renderCustomContent,
    renderCustomTopContent,
    hiddenOptions,
    onClose,
    onOpen,
    sx,
    noMargin = false,
    renderOption,
    labelSx,
    helpTopicPath,
    infoText,
    ...props
}: SelectFieldProps) {
    const inputId = useId();

    const hasCategorizedOptions = categorizedOptions && categorizedOptions.length > 0;
    const hasSimpleOptions = options && options.length > 0;

    if (!hasCategorizedOptions && !hasSimpleOptions) {
        console.warn('SelectField: No options provided');
    }

    const renderEmptyOption = () => {
        if (!emptyOption) return null;
        return (
            <MenuItem
                key={emptyOption.value}
                value={emptyOption.value}
                sx={{ fontSize: 14, lineHeight: '18.2px', ...emptyOption.sx }}
            >
                {emptyOption.label}
            </MenuItem>
        );
    };

    const renderSimpleOptions = () =>
        options.map(opt =>
            renderOption ? (
                renderOption(opt)
            ) : (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14, ...opt.sx }}>
                    {opt.label}
                    {opt.icon}
                </MenuItem>
            )
        );

    const renderCategorizedOptions = () =>
        categorizedOptions?.map(category => [
            <ListSubheader
                key={`category-${category.category}`}
                sx={{ fontSize: 12, fontWeight: 700, lineHeight: '19.92px' }}
            >
                {category.category}
            </ListSubheader>,
            ...category.options.map(option =>
                renderOption ? (
                    renderOption(option)
                ) : (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ pl: 4, fontSize: 14, ...option.sx }}
                    >
                        {option.label}
                    </MenuItem>
                )
            )
        ]);

    const labelNode = label ? (
        <InputLabel
            shrink={false}
            id={`${inputId}-label`}
            htmlFor={inputId}
            sx={{ fontSize: 12, color: 'text.secondary', ...labelSx }}
        >
            {label}
        </InputLabel>
    ) : null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            {/* The help icon sits beside the <label>, not inside it, so the button is never
                nested in a label element and the label's `htmlFor` wiring is untouched. */}
            {labelNode && helpTopicPath ? (
                <Info
                    helpTopicPath={helpTopicPath}
                    infoText={infoText}
                    iconSize={12}
                    sx={{ gap: 0.5 }}
                >
                    {labelNode}
                </Info>
            ) : (
                labelNode
            )}
            <Select
                {...props}
                size={size}
                name={name}
                id={inputId}
                /*
                 * ★ `labelId`, NOT `aria-labelledby` (ROBO-324). MUI's SelectInput does not accept
                 * that attribute — it RECOMPUTES it as `[labelId, buttonId]`. Passing
                 * `aria-labelledby` here looked correct and did nothing: with `labelId` undefined
                 * only `buttonId` survived, so the combobox was labelled by ITSELF and its
                 * accessible name collapsed to the selected option's text, empty before a choice.
                 * Every consumer in the app.
                 *
                 * Undefined when there is no label, because no InputLabel renders then and a
                 * reference to a non-existent id is worse than no name at all.
                 */
                labelId={label ? `${inputId}-label` : undefined}
                fullWidth
                readOnly={readOnly}
                onClose={onClose}
                onOpen={onOpen}
                sx={{
                    mb: noMargin ? 0 : 2,
                    '& .MuiSelect-select': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    },
                    '&.TwoLine .MuiSelect-select': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        textOverflow: 'clip',
                        minHeight: '2.8rem',
                        height: 'auto',
                        lineHeight: 1.35,
                        paddingTop: '10px',
                        paddingBottom: '10px'
                    },
                    ...(readOnly && {
                        pointerEvents: 'none',
                        '& .MuiInputBase-input': {
                            color: 'text.secondary',
                            backgroundColor: 'var(--background-content, #fff)'
                        },
                        '& .MuiInputBase-input:focus': {
                            outline: '0',
                            borderColor: 'grey.100',
                            borderWidth: '0.75px',
                            borderStyle: 'solid'
                        }
                    }),
                    ...sx
                }}
            >
                {renderCustomTopContent}
                {renderEmptyOption()}
                {hiddenOptions}
                {renderCategorizedOptions()}
                {renderSimpleOptions()}
                {renderCustomContent}
            </Select>
            {/*
             * ★ A HINT IS NOT ONLY AN ERROR (ROBO-644 Failed QA, 2026-09-29). helperText used to
             *   render only inside `props.error`, so every caller passing a plain hint got nothing:
             *   the team member page's "Add a 10-digit text message number, or this will not send",
             *   the Products filter panel's descriptions, Hubby's "No team member has used Hubby
             *   yet." The error branch is unchanged — it still renders even when the text is empty,
             *   as validation callers rely on — and a non-error hint renders only when non-empty,
             *   so callers passing '' or undefined gain no blank line.
             */}
            {props.error ? (
                <FormHelperText error>{helperText}</FormHelperText>
            ) : helperText ? (
                <FormHelperText>{helperText}</FormHelperText>
            ) : null}
        </Box>
    );
}
